/**
 * Agent API Client
 *
 * Purpose: Full API client for agent network management (Ketchup portal, mobile, agent portal).
 * Backend: /api/v1/agents (GET/POST for list, stats, by-type, :id, float, transactions, performance, rankings, liquidity/alerts).
 * Location: packages/api-client/src/ketchup/agentAPI.ts
 */

import { apiClient } from './api';
import type { PaginatedResponse } from '../types';

export interface Agent {
  id: string;
  name: string;
  type: 'small' | 'medium' | 'large';
  region: string;
  status: 'active' | 'inactive' | 'low_liquidity';
  liquidity: number;
  transactionsToday: number;
  volumeToday: number;
  successRate: number;
}

export interface AgentFilters {
  region?: string;
  status?: string;
  type?: string;
  limit?: number;
  offset?: number;
}

export interface AgentStats {
  total: number;
  active: number;
  inactive: number;
  lowLiquidity: number;
  down?: number;
  totalVolumeToday: number;
  avgSuccessRate: number;
}

export const agentAPI = {
  async getAll(filters?: AgentFilters): Promise<PaginatedResponse<Agent>> {
    return apiClient.get<PaginatedResponse<Agent>>('/agents', filters as Record<string, unknown>);
  },

  async getStats(type?: string): Promise<AgentStats> {
    return apiClient.get<AgentStats>('/agents/stats', type ? { type } : undefined);
  },

  async getNetworkStats(): Promise<Record<string, unknown>> {
    return apiClient.get<Record<string, unknown>>('/agents/network-stats');
  },

  async getByType(type: string): Promise<Agent[]> {
    const data = await apiClient.get<Agent[]>('/agents/by-type/' + encodeURIComponent(type));
    return Array.isArray(data) ? data : [];
  },

  async getById(id: string): Promise<Agent | null> {
    return apiClient.get<Agent>('/agents/' + encodeURIComponent(id)).catch(() => null);
  },

  async getFloat(id: string): Promise<Record<string, unknown> | null> {
    return apiClient.get<Record<string, unknown>>('/agents/' + encodeURIComponent(id) + '/float').catch(() => null);
  },

  async topupFloat(
    id: string,
    body: { amount: number; notes?: string; processedBy?: string; approvedBy?: string }
  ): Promise<Record<string, unknown>> {
    return apiClient.post<Record<string, unknown>>('/agents/' + encodeURIComponent(id) + '/float/topup', body);
  },

  async withdrawFloat(
    id: string,
    body: { amount: number; notes?: string; approvedBy: string }
  ): Promise<Record<string, unknown>> {
    return apiClient.post<Record<string, unknown>>('/agents/' + encodeURIComponent(id) + '/float/withdraw', body);
  },

  async getTransactions(id: string, days?: number): Promise<unknown[]> {
    const data = await apiClient.get<unknown[]>('/agents/' + encodeURIComponent(id) + '/transactions', days != null ? { days: String(days) } : undefined);
    return Array.isArray(data) ? data : [];
  },

  async getPerformance(id: string, days?: number): Promise<Record<string, unknown> | null> {
    return apiClient.get<Record<string, unknown>>('/agents/' + encodeURIComponent(id) + '/performance', days != null ? { days: String(days) } : undefined).catch(() => null);
  },

  async getPerformanceRankings(params?: { type?: string; metric?: string; limit?: number }): Promise<unknown[]> {
    const q: Record<string, string> = {};
    if (params?.type) q.type = params.type;
    if (params?.metric) q.metric = params.metric;
    if (params?.limit != null) q.limit = String(params.limit);
    const data = await apiClient.get<unknown[]>('/agents/performance/rankings', Object.keys(q).length ? q : undefined);
    return Array.isArray(data) ? data : [];
  },

  async getLiquidityAlerts(threshold?: number): Promise<unknown[]> {
    const data = await apiClient.get<unknown[]>('/agents/liquidity/alerts', threshold != null ? { threshold: String(threshold) } : undefined);
    return Array.isArray(data) ? data : [];
  },
};
